import React, { useEffect, useId, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import useQueryGetActiveTestSessions, { ITestSession } from '@/api/user/getActiveTestSessions.ts'
import useQueryGetMyPermission, { PermissionAction, PermissionSubject } from '@/api/auth/getPermission.ts'
import ClockIcon from '@/assets/ic_clock.svg?react'
import Toggle from '@/component/toggle/Toggle.tsx'
import useTestSessionActions from '@/component/testMode/useTestSessionActions.tsx'
import useCountdown from '@/util/hook/useCountdown.ts'
import { formatRemaining, formatTestClock, formatTestDateTime } from '@/util/testSession.ts'

interface IProps {
    protecteeId: number
    protecteeName: string
    hasDevice: boolean
    recentSession?: ITestSession | null
}

const EmergencyTestRow: React.FC<IProps> = ({ protecteeId, protecteeName, hasDevice, recentSession }) => {
    const { t } = useTranslation()
    const descriptionId = useId()
    const { hasPermission } = useQueryGetMyPermission()
    const { data: sessions = [] } = useQueryGetActiveTestSessions()
    const { start, end, isBusy } = useTestSessionActions()
    const active = sessions.find(session => session.protecteeId === protecteeId)
    const remaining = useCountdown(active?.endsAt)
    const [endedHere, setEndedHere] = useState<ITestSession | null>(null)
    const previousActiveRef = useRef(active)

    useEffect(() => {
        if (previousActiveRef.current && !active) setEndedHere(previousActiveRef.current)
        previousActiveRef.current = active
    }, [active])

    if (!hasPermission(PermissionSubject.ALERT_EMERGENCY, PermissionAction.UPDATE)) return null

    const isOn = Boolean(active)
    const recent = endedHere ?? (recentSession && recentSession.id !== active?.id ? recentSession : null)
    const isDisabled = isBusy(protecteeId) || (!isOn && !hasDevice)

    const onToggle = (checked: boolean) => {
        if (checked) {
            start(protecteeId)
        } else if (active) {
            end({ ...active, protecteeName })
        }
    }

    return (
        <div className={'emergency-test-row'}>
            <div className={'emergency-test-row__label'}>
                <span className={'emergency-test-row__title'}>{t('testMode.editRow.label')}</span>
                <span className={'emergency-test-row__hint'}>{t('testMode.editRow.hint')}</span>
            </div>
            <div className={`emergency-test-row__box ${isOn ? 'is-on' : ''}`}>
                <div className={'emergency-test-row__text'} id={descriptionId}>
                    {active ? (
                        <>
                            <strong>
                                {t('testMode.editRow.startedBy', {
                                    name: active.createdByName,
                                    time: formatTestClock(active.createdAt)
                                })}
                            </strong>
                            <span>{t('testMode.editRow.onGuide')}</span>
                        </>
                    ) : (
                        <>
                            <p>{t('testMode.editRow.offDesc')}</p>
                            {!hasDevice ? (
                                <span>{t('testMode.editRow.noDevice')}</span>
                            ) : (
                                recent && (
                                    <span>
                                        {t('testMode.editRow.recent', {
                                            time: formatTestDateTime(recent.createdAt),
                                            name: recent.createdByName
                                        })}
                                    </span>
                                )
                            )}
                        </>
                    )}
                </div>
                {isOn && (
                    <span className={'emergency-test-row__remaining'}>
                        <ClockIcon aria-hidden />
                        {t('testMode.remaining', { time: formatRemaining(remaining) })}
                    </span>
                )}
                <div className={'emergency-test-row__switch'}>
                    <span aria-hidden>{isOn ? t('testMode.state.on') : t('testMode.state.off')}</span>
                    <Toggle
                        checked={isOn}
                        disabled={isDisabled}
                        ariaLabel={t('testMode.editRow.label')}
                        ariaDescribedBy={descriptionId}
                        onChange={onToggle}
                    />
                </div>
            </div>
        </div>
    )
}

export default EmergencyTestRow
